// Central API config.
// Default = localhost for dev. Override with REACT_APP_API_BASE in frontend/.env
// (or in frontend/.env.production for AWS build).
export const API_BASE =
  process.env.REACT_APP_API_BASE || "http://localhost:5000";

export const SHEETS = {
  products: `${API_BASE}/api/sheets/products`,
  stock: `${API_BASE}/api/sheets/stock`,
  rings: `${API_BASE}/api/sheets/rings`,
  // Creatives bahut bada hota hai — phone se filter karke chhota response milega
  creatives: (phone) =>
    phone
      ? `${API_BASE}/api/sheets/creatives?phone=${encodeURIComponent(phone)}`
      : `${API_BASE}/api/sheets/creatives`,
  outstandings: `${API_BASE}/api/sheets/outstandings`,
  promotional: `${API_BASE}/api/sheets/promotional`,
  dealers: (phone) =>
    phone
      ? `${API_BASE}/api/sheets/dealers?phone=${encodeURIComponent(phone)}`
      : `${API_BASE}/api/sheets/dealers`,
  // Pending orders are keyed by GSTIN (the new sheet has no phone column).
  // Pass either:
  //   - a single GSTIN string, or
  //   - an array of GSTINs (will be joined with commas), or
  //   - falsy → returns the full payload (caller filters client-side)
  pendingOrders: (gst) => {
    const param = Array.isArray(gst)
      ? gst.filter(Boolean).join(",")
      : String(gst || "");
    return `${API_BASE}/api/sheets/pending-orders?gst=${encodeURIComponent(param)}`;
  },
  kycStatus: (phone) =>
    `${API_BASE}/api/sheets/kyc-status?phone=${encodeURIComponent(phone || "")}`,
  orders: (type) => `${API_BASE}/api/sheets/orders/${encodeURIComponent(type)}`,
  refresh: (key) => `${API_BASE}/api/sheets/refresh/${encodeURIComponent(key)}`,
  // CORS-enabled image proxy — needed for canvas getImageData() on Google
  // Drive / external images that don't send Access-Control-Allow-Origin.
  imgProxy: (url) =>
    `${API_BASE}/api/sheets/img-proxy?url=${encodeURIComponent(url)}`,
};

// Ask backend to pull the latest from Apps Script before we fetch.
// Non-blocking by default — caller decides whether to await.
//
// IMPORTANT: forceRefreshSheet ROUND-TRIPS to Google Apps Script which is
// SLOW (3-15s per request). The backend already auto-syncs every 30-120s
// per source, so calling this on every page action causes painful UI lag.
//
// Two safeguards built in here:
//
// 1. **Module-level rate limit** — for any given key, we only actually hit
//    the backend at most once every REFRESH_MIN_INTERVAL_MS. Repeat calls
//    within that window resolve immediately with the in-flight promise (or
//    a no-op if the last refresh is still recent).
//
// 2. **In-flight de-duplication** — if a refresh for a key is already
//    running, subsequent callers piggyback on the same promise instead of
//    triggering parallel Apps Script hits.
const REFRESH_MIN_INTERVAL_MS = 30_000; // don't hit Apps Script more than once / 30s per key
const _lastRefreshAt = new Map();       // key -> ms timestamp of last completed refresh
const _inFlight = new Map();            // key -> promise of an in-flight refresh

export async function forceRefreshSheet(key, opts = {}) {
  const { force = false } = opts;
  const now = Date.now();
  const last = _lastRefreshAt.get(key) || 0;

  // Already running? Reuse the in-flight promise.
  if (_inFlight.has(key)) return _inFlight.get(key);

  // Recently refreshed? Skip unless caller insists.
  if (!force && now - last < REFRESH_MIN_INTERVAL_MS) {
    return Promise.resolve();
  }

  const p = (async () => {
    // Client-side timeout — even if the backend is the OLD version that
    // awaits Apps Script (and takes 30+s), we abandon after 8s so the UI
    // doesn't hang. AbortController fires a 'AbortError' that we swallow.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8_000);
    try {
      await fetch(SHEETS.refresh(key), {
        method: "POST",
        signal: controller.signal,
      });
    } catch (e) {
      // swallow — UI should still read whatever is cached
    } finally {
      clearTimeout(timer);
      _lastRefreshAt.set(key, Date.now());
      _inFlight.delete(key);
    }
  })();
  _inFlight.set(key, p);
  return p;
}
